import { useEffect, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, ImageIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useFileUpload } from "@/lib/uploadProgress";

const EMPTY = { name: "", slug: "", image_url: "", sort_order: 0 };

export default function AdminProductTags() {
  const uploadFile = useFileUpload();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = () => api.get("/merchant/product-tags").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const openEdit = (c) => { setEditing({ ...EMPTY, ...c }); setOpen(true); };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "category");
    setUploading(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      setEditing((e) => ({ ...e, image_url: data.url }));
    } catch (e) { toast.error(extractErr(e)); } finally { setUploading(false); }
  };

  const save = async () => {
    try {
      const payload = {
        name: editing.name,
        slug: editing.slug || undefined,
        image_url: editing.image_url || null,
        sort_order: parseInt(editing.sort_order || 0, 10),
      };
      if (editing.id) await api.put(`/merchant/product-tags/${editing.id}`, payload);
      else await api.post("/merchant/product-tags", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const del = async (c) => {
    try {
      await api.delete(`/merchant/product-tags/${c.id}`);
      toast.success("Deleted");
      setSelected((s) => { const n = new Set(s); n.delete(c.id); return n; });
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const allSelected = items.length > 0 && items.every((c) => selected.has(c.id));
  const someSelected = items.some((c) => selected.has(c.id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) items.forEach((c) => n.delete(c.id));
    else items.forEach((c) => n.add(c.id));
    return n;
  });
  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/merchant/product-tags/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} tag${data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(extractErr(e)); } finally { setBulkDeleting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Tags</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} tag{items.length === 1 ? "" : "s"} · Reuse them on any product. Each tag can have an image.</p>
        </div>
        <Button onClick={openNew} data-testid="tags-new-btn" className="rounded-full bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> New tag</Button>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-blue-200 bg-blue-50/70 px-4 py-3" data-testid="tags-bulk-bar">
          <div className="text-sm font-medium text-slate-700"><span data-testid="tags-selected-count">{selected.size}</span> selected</div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full bg-white text-red-600 border-red-200" data-testid="tags-bulk-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete selected</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {selected.size} tag{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
                <AlertDialogDescription>Products keep their other tags.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={bulkDelete} disabled={bulkDeleting} className="bg-red-600 hover:bg-red-700" data-testid="tags-bulk-delete-confirm">{bulkDeleting ? "Deleting…" : "Delete"}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <Card className="p-0 overflow-hidden border-slate-200/70">
        {items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm" data-testid="tags-empty">No tags yet. Create one and attach it to products.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            <div className="px-4 py-2.5 flex items-center gap-4 bg-slate-50/70 text-xs font-medium text-slate-500">
              <Checkbox checked={allSelected ? true : (someSelected ? "indeterminate" : false)} onCheckedChange={toggleAll} data-testid="tags-select-all" aria-label="Select all" />
              <span>Select all</span>
            </div>
            {items.map((c) => (
              <div key={c.id} className={`p-4 flex items-center gap-4 ${selected.has(c.id) ? "bg-blue-50/40" : ""}`} data-testid={`tag-row-${c.slug}`}>
                <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} data-testid={`tag-select-${c.slug}`} />
                <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                  {c.image_url ? <img src={mediaUrl(c.image_url)} alt={c.name} className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-slate-300" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  <div className="text-xs font-mono-nums text-slate-500">/{c.slug} · {c.product_count || 0} product{(c.product_count || 0) === 1 ? "" : "s"}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`tag-edit-${c.slug}`}><Pencil className="w-4 h-4" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button variant="outline" size="sm" data-testid={`tag-delete-${c.slug}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete tag?</AlertDialogTitle><AlertDialogDescription>Products lose this tag only.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(c)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Edit tag" : "New tag"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input data-testid="tag-form-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>Slug (URL)</Label>
                <Input data-testid="tag-form-slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto" />
              </div>
              <div>
                <Label>Tag image</Label>
                <div className="mt-1 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {editing.image_url ? <img src={mediaUrl(editing.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
                  </div>
                  <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 cursor-pointer" data-testid="tag-form-image">
                    <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : (editing.image_url ? "Replace image" : "Upload image")}
                    <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                  </label>
                  {editing.image_url && <button type="button" className="text-xs text-red-600" onClick={() => setEditing({ ...editing, image_url: "" })}>Remove</button>}
                </div>
              </div>
              <div>
                <Label>Sort order</Label>
                <Input type="number" data-testid="tag-form-sort" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="tag-form-save" className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
